import React, { useEffect, useState, useCallback } from "react";
import {
  Table, TableBody, TableCell, TableContainer, TableRow,
  Paper, Grid, Card, Box, Pagination, Stack, TextField, Chip, Tabs, Tab,
} from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import { ToastContainer, toast } from "react-toastify";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import EmptyData from "../../components/EmptyData";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import SendIcon from "@mui/icons-material/Send";
import SearchIcon from "@mui/icons-material/Search";
import RefreshIcon from "@mui/icons-material/Refresh";
import DirectionsCarIcon from "@mui/icons-material/DirectionsCar";
import GpsFixedIcon from "@mui/icons-material/GpsFixed";
import CloseIcon from "@mui/icons-material/Close";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import PhoneIcon from "@mui/icons-material/Phone";
import InfoIcon from "@mui/icons-material/Info";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import RateReviewIcon from "@mui/icons-material/RateReview";
import apiClient from "../../Utils/apiClient";


const TABS = [
  { key: "all",      label: "All",           filter: null },
  { key: "pending",  label: "Pending Review", filter: (d) => d.reviewStatus === "Pending Review" },
  { key: "under",    label: "Under Review",   filter: (d) => d.reviewStatus === "Under Review" },
  { key: "reviewed", label: "Reviewed",       filter: (d) => d.reviewStatus === "Reviewed" },
  { key: "resolved", label: "Resolved",       filter: (d) => d.resolutionStatus === "Resolved" },
  { key: "rejected", label: "Rejected",       filter: (d) => d.resolutionStatus === "Rejected" },
];

const ALERT_TIMELINES = {
  AUTO_PARK_SUGGESTION: [
    "Vehicle initiated auto-park sequence",
    "Park suggestion popup sent to owner",
    "Owner responded (KEEP)",
    "Alert closed from escalation",
  ],
  VEHICLE_AUTO: [
    "Vehicle detected in GEO zone",
    "Fence alert triggered",
    "Notification dispatched to owner",
    "Admin notified for review",
  ],
  SOS: [
    "SOS button activated by device",
    "Emergency alert sent immediately",
    "Owner contacted via push notification",
    "Response team alerted",
  ],
  FENCEOUT: [
    "Vehicle exited geo-fence boundary",
    "Fence-out alert triggered",
    "Notification sent to owner",
    "Admin review initiated",
  ],
  DEVICE_REMOVED: [
    "Device removal detected",
    "Theft alert triggered automatically",
    "Engine cut command issued",
    "Owner notified via SMS & push",
  ],
  REMOVECONTINUOUSLY: [
    "Repeated removal attempt detected",
    "Multiple alerts generated",
    "Admin flagged for investigation",
    "Engine immobilized as precaution",
  ],
};
const statusChip = (status) => {
  if (status === "SENT")
    return (
      <Chip label="Sent" size="small" icon={<CheckCircleIcon style={{ fontSize: 13 }} />}
        sx={{ background: "#dcfce7", color: "#16a34a", fontWeight: 700, fontSize: 11, "& .MuiChip-icon": { color: "#16a34a" } }} />
    );
  if (status === "FAILED")
    return (
      <Chip label="Failed" size="small" icon={<ErrorIcon style={{ fontSize: 13 }} />}
        sx={{ background: "#fee2e2", color: "#dc2626", fontWeight: 700, fontSize: 11, "& .MuiChip-icon": { color: "#dc2626" } }} />
    );
  return <Chip label={status || "N/A"} size="small" />;
};

const alertStatusChip = (s) => {
  const map = {
    "Owner Attended": { bg: "#e0f2fe", color: "#0891b2" },
    "Auto Secured":   { bg: "#dcfce7", color: "#16a34a" },
    "Pending":        { bg: "#fef3c7", color: "#d97706" },
  };
  const style = map[s] || { bg: "#f3f4f6", color: "#6b7280" };
  return <Chip label={s || "Pending"} size="small" sx={{ background: style.bg, color: style.color, fontWeight: 700, fontSize: 11 }} />;
};

const reviewChip = (s) => {
  const map = {
    "Pending Review": { bg: "#fef3c7", color: "#d97706" },
    "Under Review":   { bg: "#ede9fe", color: "#7c3aed" },
    "Reviewed":       { bg: "#dcfce7", color: "#16a34a" },
    "Not Applicable": { bg: "#f3f4f6", color: "#6b7280" },
  };
  const style = map[s] || { bg: "#f3f4f6", color: "#6b7280" };
  return <Chip label={s || "N/A"} size="small" sx={{ background: style.bg, color: style.color, fontWeight: 700, fontSize: 11 }} />;
};

const resolutionChip = (s) => {
  const map = {
    "Resolved":     { bg: "#dcfce7", color: "#16a34a" },
    "Not Resolved": { bg: "#fee2e2", color: "#dc2626" },
    "Rejected":     { bg: "#fef3c7", color: "#d97706" },
  };
  const style = map[s] || { bg: "#f3f4f6", color: "#6b7280" };
  return <Chip label={s || "Not Resolved"} size="small" sx={{ background: style.bg, color: style.color, fontWeight: 700, fontSize: 11 }} />;
};

const alertTypeChip = (t) => {
  const map = {
    AUTO_PARK_SUGGESTION: { bg: "#e8f0fe", color: "#1a56db" },
    VEHICLE_AUTO:         { bg: "#ede9fe", color: "#7c3aed" },
    SOS:                  { bg: "#fee2e2", color: "#dc2626" },
    FENCEOUT:             { bg: "#e0f2fe", color: "#0891b2" },
    DEVICE_REMOVED:       { bg: "#fef3c7", color: "#d97706" },
    REMOVECONTINUOUSLY:   { bg: "#fef3c7", color: "#d97706" },
  };
  const style = map[t] || { bg: "#f3f4f6", color: "#6b7280" };
  return <Chip label={t || "N/A"} size="small" sx={{ background: style.bg, color: style.color, fontWeight: 700, fontSize: 10 }} />;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleString("en-IN", {
    day: "2-digit", month: "short", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};


const StatCard = ({ title, value, sub, icon, gradient }) => (
  <Card sx={{ borderRadius: "12px", background: gradient, p: "14px 16px", boxShadow: "0 4px 20px rgba(0,0,0,.12)", display: "flex", alignItems: "center", gap: 1.5 }}>
    <Box sx={{ width: 46, height: 46, borderRadius: "10px", background: "rgba(255,255,255,.2)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
      {React.cloneElement(icon, { sx: { fontSize: 26, color: "#fff" } })}
    </Box>
    <Box>
      <MDTypography variant="h5" sx={{ fontWeight: 800, color: "#fff", lineHeight: 1 }}>{value}</MDTypography>
      <MDTypography variant="body2" sx={{ color: "rgba(255,255,255,.9)", fontSize: 12, fontWeight: 600 }}>{title}</MDTypography>
      {sub && <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,.65)", fontSize: 10 }}>{sub}</MDTypography>}
    </Box>
  </Card>
);

const DetailPanel = ({ item, onClose, onUpdateStatus }) => {
  if (!item) return null;

  const timelineEntries = (item.timeline && item.timeline.length > 0)
    ? item.timeline
    : (ALERT_TIMELINES[item.alertType] || ["Alert triggered", "Notification sent", "Admin alerted"]).map((description, index) => ({
        action: `Step ${index + 1}`,
        description,
        performedBy: "System",
        createdAt: item.createdAt,
      }));

  const alertTitle =
    item.alertType === "FENCEOUT"           ? "Geo-Fence Movement Alert"  :
    item.alertType === "SOS"                ? "SOS Emergency Alert"       :
    item.alertType === "DEVICE_REMOVED"     ? "Device Removal Alert"      :
    item.alertType === "AUTO_PARK_SUGGESTION" ? "Auto-Park Alert"         : "Vehicle Alert";

  const badgeColor =
    item.alertStatus === "Owner Attended" ? "#16a34a" :
    item.alertStatus === "Auto Secured"   ? "#0891b2" : "#d97706";

  return (
    <Box sx={{
      position: "fixed", top: 0, right: 0, width: 420, height: "100%",
      background: "#fff", boxShadow: "-4px 0 30px rgba(0,0,0,.14)",
      zIndex: 1300, display: "flex", flexDirection: "column",
      animation: "slideIn .3s cubic-bezier(.4,0,.2,1)",
      "@keyframes slideIn": { from: { transform: "translateX(100%)" }, to: { transform: "translateX(0)" } },
    }}>
      {/* Header */}
      <Box sx={{ background: "#0f1e3c", p: "14px 18px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Box sx={{ width: 34, height: 34, borderRadius: "8px", background: "rgba(255,200,0,.2)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18 }}>⚠️</Box>
          <Box>
            <MDTypography variant="h6" sx={{ color: "#fff", fontWeight: 700, lineHeight: 1.2 }}>{alertTitle}</MDTypography>
            <MDTypography variant="caption" sx={{ color: "rgba(255,255,255,.5)", fontSize: 11 }}>IMEI: {item.imei}</MDTypography>
          </Box>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Chip label={item.alertStatus || "Pending"} size="small"
            sx={{ background: badgeColor, color: "#fff", fontWeight: 700, fontSize: 11 }} />
          <Box onClick={onClose} sx={{ cursor: "pointer", color: "rgba(255,255,255,.6)", fontSize: 20, lineHeight: 1, ml: 1 }}>✕</Box>
        </Box>
      </Box>

      {/* Body */}
      <Box sx={{ flex: 1, overflowY: "auto", p: 2 }}>
        {/* Vehicle Details */}
        <SectionTitle icon={<DirectionsCarIcon />} title="Vehicle Details" />
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 1, mb: 2 }}>
          {[
            ["Vehicle",     item.vehicleName || "N/A"],
            ["Plate Number", item.licensePlate || "N/A"],
            ["Owner Name",  item.ownerName || "N/A"],
            ["Phone",       item.phone || "N/A"],
            ["IMEI",        item.imei || "N/A"],
            ["Tracking ID", `TRK-${item.imei}`],
          ].map(([label, val]) => (
            <DetailField key={label} label={label} value={val} mono={label === "IMEI"} blue={["Plate Number","Tracking ID"].includes(label)} />
          ))}
        </Box>

        {/* Incident Details */}
        <SectionTitle icon={<InfoIcon />} title="Incident Details" />
        <Box sx={{ background: "#f8f9fb", borderRadius: "8px", p: 1.5, mb: 2 }}>
          {[
            ["Trigger",         item.notificationBody || "N/A"],
            ["Alert Type",      item.alertType || "N/A"],
            ["Alarm Code",      item.alarmCode || "N/A"],
            ["Service Mode",    item.serviceMode || "N/A"],
            ["Location",        item.lat && item.lng ? `${item.lat}, ${item.lng}` : "N/A"],
            ["Created At",      formatDate(item.createdAt)],
            ["Updated At",      formatDate(item.updatedAt)],
            ["Delivery Status", item.status || "N/A"],
            ["Review Status",   item.reviewStatus || "N/A"],
            ["Resolution",      item.resolutionStatus || "N/A"],
          ].map(([label, val]) => (
            <Box key={label} sx={{ display: "flex", gap: 1, mb: .8 }}>
              <MDTypography variant="caption" sx={{ fontWeight: 700, color: "#6b7280", minWidth: 95, flexShrink: 0, pt: .1 }}>{label}:</MDTypography>
              <MDTypography variant="caption" sx={{ color: "#111827", fontSize: 12 }}>{val}</MDTypography>
            </Box>
          ))}
          <Box sx={{ mt: 1, pt: 1, borderTop: "1px solid #e5e7eb", fontSize: 11, color: "#6b7280" }}>
            <strong>Owner:</strong> {item.ownerName || "N/A"} · {item.email || "N/A"} · {item.phone || "N/A"}
          </Box>
        </Box>

        {/* Activity Timeline */}
        <SectionTitle icon={<AccessTimeIcon />} title="Activity Timeline" />
        <Box>
          {timelineEntries.map((entry, index) => (
            <Box key={entry._id || index} sx={{ display: "flex", gap: 1.5, mb: 1, position: "relative",
              "&::before": index < timelineEntries.length - 1 ? { content: '""', position: "absolute", left: "5px", top: "16px", width: "1px", height: "calc(100% + 4px)", background: "#e5e7eb" } : {}
            }}>
              <Box sx={{ width: 12, height: 12, borderRadius: "50%", background: "#1a56db", flexShrink: 0, mt: .4, position: "relative", zIndex: 1 }} />
              <Box>
                <MDTypography variant="caption" sx={{ fontSize: 12, color: "#111827", fontWeight: 700 }}>{entry.action || `Step ${index + 1}`}</MDTypography>
                <MDTypography variant="caption" sx={{ fontSize: 11, color: "#4b5563", display: "block", mt: .3 }}>{entry.description || "No details available"}</MDTypography>
                <MDTypography variant="caption" sx={{ fontSize: 10, color: "#6b7280", mt: .3 }}>
                  {entry.performedBy ? `${entry.performedBy} · ` : ""}{formatDate(entry.createdAt || entry.updatedAt || item.createdAt)}
                </MDTypography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Update Resolution */}
        {/* <SectionTitle icon={<RateReviewIcon />} title="Update Resolution" /> */}
        {/* <Box sx={{ display: "flex", gap: 1 }}>
          <Chip label="Mark Resolved" size="small" clickable onClick={() => onUpdateStatus(item._id, "Resolved")}
            sx={{ background: "#16a34a", color: "#fff", fontWeight: 700 }} />
          <Chip label="Reject" size="small" clickable onClick={() => onUpdateStatus(item._id, "Rejected")}
            sx={{ background: "#dc2626", color: "#fff", fontWeight: 700 }} />
          <Chip label="Under Review" size="small" clickable onClick={() => onUpdateStatus(item._id, "Under Review")}
            sx={{ background: "#7c3aed", color: "#fff", fontWeight: 700 }} />
        </Box> */}
      </Box>

      {/* Footer */}
      <Box sx={{ p: "12px 16px", borderTop: "1px solid #e5e7eb", display: "flex", gap: 1 }}>
        <Box component="button" onClick={() => window.open(`https://maps.google.com/?q=${item.lat},${item.lng}`, "_blank")}
          sx={{ flex: 1, p: "10px", borderRadius: "8px", border: "none", background: "#1a56db", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: .5 }}>
          <LocationOnIcon sx={{ fontSize: 16 }} /> View Location
        </Box>
        <Box component="button" onClick={() => window.open(`tel:${item.phone}`)}
          sx={{ flex: 1, p: "10px", borderRadius: "8px", border: "none", background: "#16a34a", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: .5 }}>
          <PhoneIcon sx={{ fontSize: 16 }} /> Call Owner
        </Box>
        <Box component="button" onClick={onClose}
          sx={{ flex: 1, p: "10px", borderRadius: "8px", border: "none", background: "#dc2626", color: "#fff", fontWeight: 700, fontSize: 12, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: .5 }}>
          <CloseIcon sx={{ fontSize: 16 }} /> Close
        </Box>
      </Box>
    </Box>
  );
};

const SectionTitle = ({ icon, title }) => (
  <Box sx={{ display: "flex", alignItems: "center", gap: .8, mb: 1, mt: 1 }}>
    {React.cloneElement(icon, { sx: { fontSize: 14, color: "#6b7280" } })}
    <MDTypography variant="caption" sx={{ fontWeight: 700, textTransform: "uppercase", letterSpacing: ".8px", color: "#6b7280", fontSize: 11 }}>{title}</MDTypography>
  </Box>
);

const DetailField = ({ label, value, mono, blue }) => (
  <Box sx={{ background: "#f8f9fb", borderRadius: "7px", p: "8px 10px" }}>
    <MDTypography variant="caption" sx={{ color: "#6b7280", fontWeight: 600, fontSize: 10, display: "block", mb: .3 }}>{label}</MDTypography>
    <MDTypography variant="caption" sx={{ fontSize: 12, fontWeight: 600, color: blue ? "#1a56db" : "#111827", fontFamily: mono ? "monospace" : "inherit" }}>{value}</MDTypography>
  </Box>
);


const NotificationLog = () => {
  const [allData,       setAllData]       = useState([]);
  const [page,          setPage]          = useState(1);
  const [limit,         setLimit]         = useState(10);
  const [totalPages,    setTotalPages]    = useState(1);
  const [total,         setTotal]         = useState(0);
  const [selectedStatus, setSelectedStatus] = useState("All");
  const [searchText,    setSearchText]    = useState("");
  const [activeTab,     setActiveTab]     = useState("all");
  const [selectedItem,  setSelectedItem]  = useState(null);
  const [stats, setStats] = useState({ total: 0, sent: 0, failed: 0, review: 0, resolved: 0 });

  useEffect(() => { getData(page, limit); }, [page, limit, selectedStatus]);

  useEffect(() => {
    const timer = setTimeout(() => { setPage(1); getData(1, limit); }, 500);
    return () => clearTimeout(timer);
  }, [searchText]);

  const getData = async (pg, lmt) => {
    try {
      const params = { page: pg, limit: lmt, status: selectedStatus !== "All" ? selectedStatus : "" };
      const response = await apiClient.get("/notifications", { params });

      const data       = response?.data || [];
      const pagination = response?.pagination || {};

      setAllData(data);
      setTotalPages(pagination.totalPages || 1);
      setTotal(pagination.total || 0);

      setStats({
        total:    pagination.total || data.length,
        sent:     data.filter((d) => d.status === "SENT").length,
        failed:   data.filter((d) => d.status === "FAILED").length,
        review:   data.filter((d) => ["Pending Review", "Under Review"].includes(d.reviewStatus)).length,
        resolved: data.filter((d) => d.resolutionStatus === "Resolved").length,
      });
    } catch (error) {
      toast.error(error.message || "Failed to fetch notifications");
      setAllData([]);
    }
  };

  const updateStatus = async (id, alertStatus) => {
    try {
      await apiClient.put(`/notifications/${id}/status`, { alertStatus });
      toast.success("Status updated");
      getData(page, limit);
      // update selected item in panel
      setSelectedItem((prev) => prev && prev._id === id ? { ...prev, resolutionStatus: alertStatus, reviewStatus: alertStatus === "Under Review" ? "Under Review" : prev.reviewStatus } : prev);
    } catch (error) {
      toast.error(error.message || "Failed to update status");
    }
  };

  const filteredData = (() => {
    let d = [...allData];
    const tab = TABS.find((t) => t.key === activeTab);
    if (tab?.filter) d = d.filter(tab.filter);
    if (searchText) {
      const q = searchText.toLowerCase();
      d = d.filter((x) =>
        (x.imei || "").toLowerCase().includes(q) ||
        (x.vehicleName || "").toLowerCase().includes(q) ||
        (x.alertType || "").toLowerCase().includes(q) ||
        (x.ownerName || "").toLowerCase().includes(q)
      );
    }
    return d;
  })();

  const tabCount = (tab) => (tab.filter ? allData.filter(tab.filter).length : allData.length);

  const statCards = [
    { title: "Total Notifications", value: stats.total, sub: "All alerts in system",     icon: <NotificationsIcon />, gradient: "linear-gradient(135deg,#1a56db,#3b82f6)" },
    { title: "Sent",                value: stats.sent,  sub: "Delivered successfully",   icon: <SendIcon />,          gradient: "linear-gradient(135deg,#16a34a,#4ade80)" },
    { title: "Failed",              value: stats.failed,sub: "Failed to deliver",        icon: <ErrorIcon />,         gradient: "linear-gradient(135deg,#dc2626,#f87171)" },
    { title: "Review Status",       value: stats.review,sub: "Pending + Under Review",   icon: <RateReviewIcon />,    gradient: "linear-gradient(135deg,#7c3aed,#a78bfa)" },
    { title: "Resolution Status",   value: stats.resolved,sub:"Resolved cases",          icon: <CheckCircleIcon />,   gradient: "linear-gradient(135deg,#0891b2,#38bdf8)" },
  ];

  const TABLE_COLS = [
    "#",
    "Vehicle Name",
    "Service Mode",
    "Alert Type",
    { label: "Notification Body", hideXs: true },
    { label: "Location", hideXs: true },
    "Delivery Status",
    "Date & Time",
    "Alert Status",
    "Review Status",
    { label: "Resolution", hideXs: true },
    "Actions",
  ];

  return (
    <DashboardLayout>
      <ToastContainer />
      <MDBox pt={9} pb={3}>
        <Grid container spacing={2}>
          <Grid item xs={12}>

            {/* ── TOP ROW: stats + guide ── */}
            <Box sx={{ display: "flex", flexDirection: { xs: "column", lg: "row" }, gap: 2, mb: 2, alignItems: "stretch" }}>
              {/* Stat Cards */}
              <Box sx={{ flex: 1, display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2,1fr)", md: "repeat(3,1fr)", xl: "repeat(5,1fr)" }, gap: 1.5 }}>
                {statCards.map((c, i) => <StatCard key={i} {...c} />)}
              </Box>
              {/* Service Mode Guide */}
              <Box sx={{ width: { xs: "100%", sm: 340, md: 280 }, flexShrink: 0, background: "#fff", borderRadius: "12px", p: 2, boxShadow: "0 1px 3px rgba(0,0,0,.08)", border: "1px solid #e5e7eb" }}>
                <MDTypography variant="caption" sx={{ fontWeight: 700, fontSize: 12, display: "block", mb: 1 }}>⚙️ Service Mode Guide</MDTypography>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, mb: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#0891b2", flexShrink: 0, mt: .4 }} />
                  <MDTypography variant="caption" sx={{ fontSize: 11, color: "#374151" }}>
                    <strong style={{ color: "#0891b2" }}>Self Service:</strong> Owner manages the alert (no admin intervention)
                  </MDTypography>
                </Box>
                <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1 }}>
                  <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: "#7c3aed", flexShrink: 0, mt: .4 }} />
                  <MDTypography variant="caption" sx={{ fontSize: 11, color: "#374151" }}>
                    <strong style={{ color: "#7c3aed" }}>Customer Service:</strong> Admin will review and resolve
                  </MDTypography>
                </Box>
              </Box>
            </Box>

            {/* ── MAIN CARD ── */}
            <Card sx={{ borderRadius: "14px", overflow: "hidden", boxShadow: "0 2px 12px rgba(0,0,0,.08)" }}>

              {/* Blue header */}
              <MDBox mx={2} mt={-1} py={2} px={2} variant="gradient" bgColor="info"
                borderRadius="lg" coloredShadow="info"
                display="flex" justifyContent="space-between" alignItems={{ xs: "flex-start", md: "center" }} flexDirection={{ xs: "column", md: "row" }} gap={1.5}>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                  <NotificationsIcon sx={{ color: "white", fontSize: 20 }} />
                  <MDTypography variant="h6" color="white">Notification Log</MDTypography>
                  <MDTypography variant="caption" color="white" sx={{ opacity: .75, ml: { xs: 0, md: 1 } }}>
                    View, manage and resolve all vehicle alerts
                  </MDTypography>
                </Box>
                <Box display="flex" alignItems="center" gap={1} flexWrap="wrap" sx={{ width: { xs: "100%", md: "auto" } }}>
                  <MDTypography variant="caption" color="white" sx={{ opacity: .85 }}>
                    Total: {total} &nbsp;|&nbsp; Last Updated: Just now
                  </MDTypography>
                  <RefreshIcon
                    sx={{ color: "white", fontSize: 18, cursor: "pointer", opacity: .8, "&:hover": { opacity: 1 } }}
                    onClick={() => getData(page, limit)}
                  />
                </Box>
              </MDBox>

              {/* Tabs */}
              <Box sx={{ borderBottom: "1px solid #e5e7eb", px: 2 }}>
                <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v)}
                  sx={{ "& .MuiTab-root": { fontSize: 12, fontWeight: 600, minHeight: 42, textTransform: "none", px: 1.5 } }}>
                  {TABS.map((t) => (
                    <Tab key={t.key} value={t.key}
                      label={
                        <Box display="flex" alignItems="center" gap={.6}>
                          {t.label}
                          <Chip label={tabCount(t)} size="small"
                            sx={{ height: 18, fontSize: 10, fontWeight: 700, px: .3,
                              background: activeTab === t.key ? "#1a56db" : "#e8f0fe",
                              color: activeTab === t.key ? "#fff" : "#1a56db" }} />
                        </Box>
                      }
                    />
                  ))}
                </Tabs>
              </Box>

              {/* Toolbar */}
              <Box display="flex" flexDirection={{ xs: "column", md: "row" }} alignItems={{ xs: "stretch", md: "center" }} gap={1.5} px={2} py={1.2} sx={{ borderBottom: "1px solid #f3f4f6" }}>
                {/* Search */}
                <Box sx={{ position: "relative", width: { xs: "100%", sm: 300 } }}>
                  <SearchIcon sx={{ position: "absolute", left: 9, top: "50%", transform: "translateY(-50%)", fontSize: 16, color: "#9ca3af" }} />
                  <TextField
                    placeholder="Search IMEI, vehicle name, alert type…"
                    variant="outlined" size="small" value={searchText}
                    onChange={(e) => setSearchText(e.target.value)}
                    sx={{ width: "100%", "& .MuiOutlinedInput-root": { pl: 3.5, fontSize: 12, borderRadius: "8px", background: "#f9fafb" } }}
                  />
                </Box>
                {/* Status filter */}
                <TextField label="Delivery Status" select size="small" value={selectedStatus}
                  onChange={(e) => { setSelectedStatus(e.target.value); setPage(1); }}
                  sx={{ width: { xs: "100%", sm: 150 }, "& .MuiInputBase-root": { fontSize: 12 } }}
                  SelectProps={{ native: true }}>
                  <option value="All">All</option>
                  <option value="SENT">Sent</option>
                  <option value="FAILED">Failed</option>
                </TextField>
                {/* Rows per page */}
                <TextField label="Rows" select size="small" value={limit}
                  onChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                  sx={{ width: { xs: "100%", sm: 80 }, "& .MuiInputBase-root": { fontSize: 12 } }}
                  SelectProps={{ native: true }}>
                  {[10, 20, 50].map((n) => <option key={n} value={n}>{n}</option>)}
                </TextField>
                <MDTypography variant="caption" sx={{ ml: { xs: 0, md: "auto" }, color: "#6b7280", fontSize: 12 }}>
                  Showing {filteredData.length} of {allData.length} notifications
                </MDTypography>
              </Box>

              {/* Table */}
              {filteredData.length > 0 ? (
                <MDBox>
                  <TableContainer component={Paper} elevation={0} sx={{ overflowX: "auto" }}>
                    <Table sx={{ minWidth: 1100, "& td, & th": { fontSize: 12, padding: "8px 10px" } }}>
                      <TableBody>
                        {/* Header row */}
                        <TableRow sx={{ background: "#f8f9fb" }}>
                          {TABLE_COLS.map((col, index) => {
                            const label = typeof col === "string" ? col : col.label;
                            const hideXs = typeof col === "object" && col.hideXs;
                            return (
                              <TableCell key={`${label}-${index}`} align="center"
                                sx={{
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                  color: "#6b7280",
                                  fontSize: 11,
                                  borderBottom: "2px solid #e5e7eb",
                                  display: hideXs ? { xs: "none", md: "table-cell" } : "table-cell"
                                }}>
                                {label}
                              </TableCell>
                            );
                          })}
                        </TableRow>

                        {/* Data rows */}
                        {filteredData.slice((page - 1) * limit, page * limit).map((item, index) => (
                          <TableRow key={item?._id || index}
                            onClick={() => setSelectedItem(item)}
                            sx={{ cursor: "pointer",
                              background: selectedItem?._id === item._id ? "#eef4ff" : "transparent",
                              "&:hover": { background: "#fafbff" },
                              "& td": { borderBottom: "1px solid #f3f4f6" }
                            }}>
                            <TableCell align="center" sx={{ color: "#9ca3af", fontFamily: "monospace" }}>
                              {(page - 1) * limit + index + 1}
                            </TableCell>

                            <TableCell align="center">
                              <MDTypography variant="caption" sx={{ fontWeight: 700, display: "block", color: "#111827" }}>{item?.vehicleName || "N/A"}</MDTypography>
                              <MDTypography variant="caption" sx={{ fontSize: 10, color: "#9ca3af", fontFamily: "monospace" }}>{item?.imei || "N/A"}</MDTypography>
                            </TableCell>

                            <TableCell align="center">
                              <MDTypography variant="caption" sx={{ fontWeight: 700, display: "block",
                                color: item?.serviceMode === "Self Service" ? "#0891b2" : "#7c3aed", fontSize: 11 }}>
                                {item?.serviceMode === "Self Service" ? "⚙ Self Service" : "👤 Customer Service"}
                              </MDTypography>
                              <MDTypography variant="caption" sx={{ fontSize: 10, color: "#9ca3af" }}>
                                {item?.serviceMode === "Self Service" ? "No Support" : "With Support"}
                              </MDTypography>
                            </TableCell>

                            <TableCell align="center">{alertTypeChip(item?.alertType)}</TableCell>

                            <TableCell align="center" sx={{ maxWidth: 170, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", display: { xs: "none", md: "table-cell" } }}
                              title={item?.notificationBody}>
                              {item?.notificationBody || "N/A"}
                            </TableCell>

                            <TableCell align="center" sx={{ display: { xs: "none", md: "table-cell" } }}>
                              {item?.lat && item?.lng ? (
                                <Box>
                                  <MDTypography variant="caption" sx={{ display: "block", fontSize: 11 }}>
                                    {Number(item.lat).toFixed(4)}, {Number(item.lng).toFixed(4)}
                                  </MDTypography>
                                  <MDTypography variant="caption"
                                    onClick={(e) => { e.stopPropagation(); window.open(`https://maps.google.com/?q=${item.lat},${item.lng}`, "_blank"); }}
                                    sx={{ fontSize: 10, color: "#1a56db", cursor: "pointer", textDecoration: "underline" }}>
                                    View on Map
                                  </MDTypography>
                                </Box>
                              ) : "N/A"}
                            </TableCell>

                            <TableCell align="center">{statusChip(item?.status)}</TableCell>
                            <TableCell align="center" sx={{ whiteSpace: "nowrap", fontSize: 11 }}>{formatDate(item?.createdAt)}</TableCell>
                            <TableCell align="center">{alertStatusChip(item?.alertStatus)}</TableCell>
                            <TableCell align="center">{reviewChip(item?.reviewStatus)}</TableCell>
                            <TableCell align="center" sx={{ display: { xs: "none", md: "table-cell" } }}>{resolutionChip(item?.resolutionStatus)}</TableCell>

                            <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                              <Box display="flex" gap={.6} justifyContent="center">
                                {item?.reviewStatus === "Pending Review" ? (
                                  <Chip label="Review ▾" size="small" clickable
                                    onClick={() => updateStatus(item._id, "Under Review")}
                                    sx={{ background: "#fef3c7", color: "#d97706", fontWeight: 700, fontSize: 11 }} />
                                ) : (
                                  <Chip label="View Details" size="small" clickable
                                    onClick={() => setSelectedItem(item)}
                                    sx={{ background: "#e8f0fe", color: "#1a56db", fontWeight: 700, fontSize: 11 }} />
                                )}
                                <Chip label="Resolve" size="small" clickable
                                  onClick={() => updateStatus(item._id, "Resolved")}
                                  sx={{ background: "#dcfce7", color: "#16a34a", fontWeight: 700, fontSize: 11 }} />
                              </Box>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>

                  {/* Pagination */}
                  <Box display="flex" justifyContent="space-between" alignItems="center" px={2} py={1.2}
                    sx={{ borderTop: "1px solid #f3f4f6" }}>
                    <MDTypography variant="caption" color="text">
                      Showing {(page - 1) * limit + 1} to {Math.min(page * limit, filteredData.length)} of {filteredData.length} notifications
                    </MDTypography>
                    <Pagination count={totalPages} page={page} onChange={(_, v) => setPage(v)} color="primary" size="small" />
                  </Box>

                  {/* Legend */}
                  <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, px: 2, py: 1.2, borderTop: "1px solid #f3f4f6", alignItems: "center" }}>
                    {[
                      { label: "Service Mode:", items: [{ c:"#0891b2",l:"Self Service"},{c:"#7c3aed",l:"Customer Service"}]},
                      { label: "Alert Status:", items: [{ c:"#f59e0b",l:"Pending"},{c:"#0891b2",l:"Owner Attended"},{c:"#16a34a",l:"Auto Secured"}]},
                      { label: "Review:", items: [{ c:"#d97706",l:"Pending Review"},{c:"#7c3aed",l:"Under Review"},{c:"#16a34a",l:"Reviewed"}]},
                      { label: "Resolution:", items: [{ c:"#dc2626",l:"Not Resolved"},{c:"#16a34a",l:"Resolved"},{c:"#f59e0b",l:"Rejected"}]},
                    ].map((grp) => (
                      <Box key={grp.label} display="flex" alignItems="center" gap={1}>
                        <MDTypography variant="caption" sx={{ fontWeight: 700, fontSize: 11, color: "#374151" }}>{grp.label}</MDTypography>
                        {grp.items.map((it) => (
                          <Box key={it.l} display="flex" alignItems="center" gap={.4}>
                            <Box sx={{ width: 8, height: 8, borderRadius: "50%", background: it.c }} />
                            <MDTypography variant="caption" sx={{ fontSize: 10, color: "#6b7280" }}>{it.l}</MDTypography>
                          </Box>
                        ))}
                      </Box>
                    ))}
                  </Box>
                </MDBox>
              ) : (
                <EmptyData />
              )}
            </Card>
          </Grid>
        </Grid>
      </MDBox>

      {/* Detail Side Panel */}
      {selectedItem && (
        <>
          <Box onClick={() => setSelectedItem(null)}
            sx={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.3)", zIndex: 1200 }} />
          <DetailPanel
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
            onUpdateStatus={(id, status) => { updateStatus(id, status); }}
          />
        </>
      )}
    </DashboardLayout>
  );
};

export default NotificationLog;